import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, MessageSquareText } from 'lucide-react';
import { orderBy, limit } from 'firebase/firestore';
import {
  canProject,
  TASK_STATUSES,
  type Deliverable,
  type DocumentPointer,
  type Regeneration,
  type Task,
  type TaskStatus,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { asDate } from '../../lib/time.js';
import { useProject } from './ProjectProvider.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { LatestProgressDialog } from './LatestProgressDialog.js';
import { AskProjectDialog } from './AskProjectDialog.js';
import { CLIENT_FOLDER_LABEL } from './NewProjectDialog.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { TrainingOverviewCards } from '../training/TrainingOverviewCards.js';

const ACTIVE_STATUSES = ['backlog', 'in_progress', 'on_hold', 'blocked'] as const;
const WRAP_UP_STATUSES = ['testing', 'in_review', 'done', 'canceled'] as const;

const LATEST_REGENERATION = [orderBy('createdAt', 'desc'), limit(1)];

function StageCard({
  title,
  to,
  status,
  detail,
}: {
  title: string;
  to: string;
  status: 'none' | 'draft' | 'accepted';
  detail: string;
}) {
  const done = status === 'accepted';
  return (
    <Link to={to}>
      <Card
        className={`flex flex-col gap-2 p-4 hover:border-[var(--ink-400)] ${done ? 'border-[color-mix(in_srgb,var(--success)_35%,var(--border))]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
              done
                ? 'bg-success text-white'
                : status === 'draft'
                  ? 'bg-accent-soft text-accent-soft-fg'
                  : 'border border-border text-muted'
            }`}
          >
            {done ? '✓' : status === 'draft' ? '…' : ''}
          </span>
          <span className="font-medium">{title}</span>
        </div>
        <p className="text-xs text-muted">{detail}</p>
      </Card>
    </Link>
  );
}

function LinkSlot({ title, url, to }: { title: string; url?: string; to: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 p-4">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{title}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="truncate text-sm font-medium text-accent"
        >
          {url}
        </a>
      ) : (
        <Link to={to} className="text-sm text-muted hover:text-text">
          Not added yet — add in Deliverables
        </Link>
      )}
    </div>
  );
}

function StatusCard({
  title,
  statuses,
  counts,
  total,
}: {
  title: string;
  statuses: readonly TaskStatus[];
  counts: Record<string, number>;
  total: number;
}) {
  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="text-sm font-semibold">{title}</h2>
      <div className="flex flex-col gap-2">
        {statuses.map((s) => (
          <div key={s} className="flex items-center gap-3">
            <div className="w-28 shrink-0 sm:w-32">
              <StatusBadge status={s} />
            </div>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: total ? `${((counts[s] ?? 0) / total) * 100}%` : '0%' }}
              />
            </div>
            <span className="w-6 text-right font-mono text-xs text-muted">{counts[s] ?? 0}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ProjectOverviewPage() {
  const { projectId, project, role } = useProject();
  const [progressOpen, setProgressOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  // Same gate the API's /ask route (and the progress summary) enforces: members and up.
  const canAsk = canProject(role, 'project.activity.read');
  const { data: prdPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/prd`);
  const { data: tddPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/tdd`);
  const { data: tasks } = useCollection<Task>(`projects/${projectId}/tasks`);
  const { data: regenerations } = useCollection<Regeneration>(
    `projects/${projectId}/regenerations`,
    LATEST_REGENERATION,
  );
  const { data: deliverables } = useCollection<Deliverable>(`projects/${projectId}/deliverables`);
  const latestRegeneration = regenerations[0];
  const needsRegenerationReview = latestRegeneration
    ? latestRegeneration.status !== 'completed'
    : false;

  const counts = Object.fromEntries(TASK_STATUSES.map((s) => [s, 0])) as Record<string, number>;
  for (const t of tasks) counts[t.status] = (counts[t.status] ?? 0) + 1;
  const total = tasks.length;
  const done = counts.done ?? 0;
  const blocked = counts.blocked ?? 0;

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {/* On a phone the title takes its own row and the actions/stats wrap beneath it, rather
          than all four items squeezing onto one line. */}
      <div className="flex flex-wrap items-baseline gap-3 sm:gap-6">
        <div className="w-full min-w-0 sm:w-auto">
          <h1 className="break-words font-heading font-semibold text-2xl">{project?.name}</h1>
          <p className="mt-1 text-xs text-muted">
            {project?.startDate}
            {project?.endDate ? ` → ${project.endDate}` : ''}
          </p>
        </div>
        <Button onClick={() => setProgressOpen(true)}>
          <Activity className="mr-1.5 h-3.5 w-3.5" />
          Latest progress
        </Button>
        {canAsk && (
          <Button onClick={() => setAskOpen(true)}>
            <MessageSquareText className="mr-1.5 h-3.5 w-3.5" />
            Ask this project
          </Button>
        )}
        <div className="ml-auto flex gap-6 text-right">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted">
              Completion
            </div>
            <div className="font-heading font-semibold text-2xl">
              {total ? Math.round((done / total) * 100) : 0}%
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-danger">
              Blocked
            </div>
            <div className="font-heading font-semibold text-2xl text-danger">{blocked}</div>
          </div>
        </div>
      </div>

      {needsRegenerationReview && (
        <Link
          to={`/projects/${projectId}/regenerations`}
          className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm hover:border-[var(--ink-400)]"
        >
          A document changed since tasks were generated —{' '}
          <span className="font-medium text-accent">review regeneration impact →</span>
        </Link>
      )}

      <Card className="grid grid-cols-1 divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <LinkSlot
          title="Client folder · Google Drive"
          url={
            deliverables.find(
              (d) =>
                d.kind === 'client_folder' ||
                (d.kind === 'link' && d.label === CLIENT_FOLDER_LABEL),
            )?.url
          }
          to={`/projects/${projectId}/deliverables`}
        />
        <LinkSlot
          title="Deployment URL"
          url={deliverables.find((d) => d.kind === 'deployment')?.url}
          to={`/projects/${projectId}/deliverables`}
        />
        <LinkSlot
          title="Cloud project"
          url={deliverables.find((d) => d.kind === 'cloud_project')?.url}
          to={`/projects/${projectId}/deliverables`}
        />
      </Card>

      {project?.projectType === 'training' ? (
        <TrainingOverviewCards projectId={projectId} />
      ) : (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <StageCard
            title="PRD"
            to={`/projects/${projectId}/prd`}
            status={prdPointer?.status ?? 'none'}
            detail={
              prdPointer?.status === 'accepted'
                ? `v${prdPointer.currentVersionNumber} accepted`
                : prdPointer?.status === 'draft'
                  ? 'Draft awaiting review'
                  : 'Not started'
            }
          />
          <StageCard
            title="TDD"
            to={`/projects/${projectId}/tdd`}
            status={tddPointer?.status ?? 'none'}
            detail={
              tddPointer?.status === 'accepted'
                ? `v${tddPointer.currentVersionNumber} accepted`
                : tddPointer?.status === 'draft'
                  ? 'Draft awaiting review'
                  : prdPointer?.status === 'accepted'
                    ? 'Ready to generate'
                    : 'Accept the PRD first'
            }
          />
          <StageCard
            title="Tasks"
            to={`/projects/${projectId}/tasks`}
            status={total > 0 ? 'accepted' : 'none'}
            detail={total > 0 ? `${total} tasks, ${done} done` : 'Not generated yet'}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <StatusCard
          title="Work in flight"
          statuses={ACTIVE_STATUSES}
          counts={counts}
          total={total}
        />
        <StatusCard
          title="Review & wrap-up"
          statuses={WRAP_UP_STATUSES}
          counts={counts}
          total={total}
        />
      </div>

      <LatestProgressDialog
        open={progressOpen}
        onOpenChange={setProgressOpen}
        projectId={projectId}
        tasks={tasks}
      />
      <AskProjectDialog
        open={askOpen}
        onOpenChange={setAskOpen}
        projectId={projectId}
        canIncludeDiscussions={canProject(role, 'discussion.participate')}
      />

      <Card className="flex flex-col gap-3 p-4">
        <div className="flex items-center">
          <h2 className="text-sm font-semibold">Deliverables ({deliverables.length})</h2>
          <Link
            to={`/projects/${projectId}/deliverables`}
            className="ml-auto text-xs font-medium text-accent"
          >
            View all →
          </Link>
        </div>
        {deliverables.length === 0 ? (
          <p className="text-xs text-muted">Nothing delivered yet.</p>
        ) : (
          <div className="flex flex-col divide-y divide-border">
            {[...deliverables]
              // createdAt arrives as a Firestore Timestamp, not the ISO string the shared type
              // claims — compare as dates.
              .sort((a, b) => asDate(b.createdAt).getTime() - asDate(a.createdAt).getTime())
              .slice(0, 5)
              .map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="flex-1 truncate">{d.label}</span>
                  <span className="shrink-0 text-xs capitalize text-muted">
                    {d.kind.replace('_', ' ')}
                  </span>
                </div>
              ))}
          </div>
        )}
      </Card>
    </div>
  );
}
